/**
 * @license Copyright (c) 2003-2019, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import DecoupledDocumentEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor.js';

import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder.js';
import CKFinderUploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor.js';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily.js';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize.js';
import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight.js';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline.js';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';
import Indent from '@ckeditor/ckeditor5-indent/src/indent.js';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import List from '@ckeditor/ckeditor5-list/src/list.js';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat.js';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';
import TodoList from '@ckeditor/ckeditor5-list/src/todolist';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
import WordCount from '@ckeditor/ckeditor5-word-count/src/wordcount.js';

export default class DecoupledEditor extends DecoupledDocumentEditor {}

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';

class DollarPlugin extends Plugin {
   init() {
      const editor = this.editor;

      editor.model.schema.register('dollartag', { allowWhere: '$text', allowContentOf: '$block', isObject: true, allowAttributes: ['id', 'marker'] });
      editor.conversion.elementToElement({model: 'dollartag', view: 'dollartag'});
      editor.conversion.attributeToAttribute({model: 'id', view: 'id'});
      editor.conversion.attributeToAttribute({model: 'marker', view: 'marker'});
   }
}

// Plugins to include in the build.
DecoupledEditor.builtinPlugins = [
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	CKFinder,
	CKFinderUploadAdapter,
	Essentials,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	Heading,
	Highlight,
	HorizontalLine,
	Image,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	List,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	RemoveFormat,
	Strikethrough,
	Table,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	WordCount,
   DollarPlugin
];

// Editor configuration.
DecoupledEditor.defaultConfig = {
   heading: {
      options: [
         { model: 'paragraph', title: 'Paragraph'},
         { model: 'heading1',  view: { name: 'h1', classes: 'ck-heading_heading1'  }, title: 'Heading 1' },
         { model: 'heading2',  view: { name: 'h2', classes: 'ck-heading_heading2'  }, title: 'Heading 2' },
         { model: 'heading3',  view: { name: 'h3', classes: 'ck-heading_heading3'  }, title: 'Heading 3' }
      ]
   },
   toolbar: {
      items: [
         'heading',
         '|',
         'fontSize',
         'fontFamily',
         '|',
         'bold',
         'italic',
         'underline',
         'strikethrough',
         'alignment',
         '|',
         'numberedList',
         'bulletedList',
         'todoList',
         'indent',
         'outdent',
         '|',
         'fontColor',
         'fontBackgroundColor',
         '|',
         'blockQuote',
         'insertTable',
         'imageUpload',
         '|',
         'undo',
         'redo',
         'removeFormat',
      ]
   },
   image: {
      styles: [
         'full',
         'alignLeft',
         'alignRight'
      ],
      toolbar: [
         'imageStyle:alignLeft',
         'imageStyle:full',
         'imageStyle:alignRight',
         '|',
         'imageTextAlternative'
      ]
   },
   table: {
      contentToolbar: [
         'tableColumn',
         'tableRow',
         'mergeTableCells',
         "tableProperties",
         "tableCellProperties"
      ]
   },
   // This value must be kept in sync with the language defined in webpack.config.js.
   language: 'en'
};
